import { useEffect, useRef, useState, type CSSProperties, type JSX } from 'react';
import type { ViewTarget } from '../../render/api';
import { useStudio } from '../context';
import { errorMessage } from '../safe';

/** Backing stores above this edge length cost GPU memory without visible gain. */
const MAX_BACKING_EDGE = 4096;

export function backingSize(cssWidth: number, cssHeight: number, dpr: number): { width: number; height: number } {
  const scale = Math.min(dpr > 0 ? dpr : 1, MAX_BACKING_EDGE / Math.max(1, cssWidth, cssHeight));
  return { width: Math.max(1, Math.round(cssWidth * scale)), height: Math.max(1, Math.round(cssHeight * scale)) };
}

export interface ViewCanvasProps {
  target: ViewTarget;
  maxFps?: number;
  className?: string;
  style?: CSSProperties;
  label: string;
}

/** Mounts a compositor view and keeps its backing size in step with the element. */
export function ViewCanvas({ target, maxFps, className, style, label }: ViewCanvasProps): JSX.Element {
  const studio = useStudio();
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const targetKey = JSON.stringify(target);

  useEffect(() => {
    const node = host.current;
    if (!node) return;
    let view;
    try {
      view = studio.createView(JSON.parse(targetKey) as ViewTarget);
    } catch (e) {
      setError(errorMessage(e));
      return;
    }
    setError(null);
    if (maxFps) view.setMaxFps(maxFps);
    view.canvas.setAttribute('aria-hidden', 'true');
    node.appendChild(view.canvas);

    const resize = (): void => {
      const rect = node.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      const size = backingSize(rect.width, rect.height, window.devicePixelRatio);
      view.setSize(size.width, size.height);
    };
    resize();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    observer?.observe(node);
    return () => {
      observer?.disconnect();
      view.canvas.remove();
      view.destroy();
    };
  }, [studio, targetKey, maxFps]);

  return (
    <div ref={host} className={`ui-view-host ${className ?? ''}`} style={style} role="img" aria-label={label}>
      {error ? <div className="ui-empty ui-error-text">{error}</div> : null}
    </div>
  );
}
